import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { Store } from '@ngrx/store';

import { CaAddItemProcessesComponent } from '@components/products/ca-numbers/manage-ca-numbers/ca-number-item-processes/ca-add-item-processes/ca-add-item-processes.component';
import { FormatCellValuePipe } from '@components/products/item-processes/pipes/format-cell-value.pipe';
import { filter } from 'rxjs';

import { InputValidationService } from '../../../../@core/services/input-validation.service';
import { singleOrderDetails } from '../../../../@store/orders/orders.model';
import { ItemProcessPayload } from '../../../../@store/products/ca-numbers/ca-numbers.model';
import { selectItemProcessCategories } from '../../../../@store/products/item-processes/item-processes.selectors';
import { ItemProcessCategory } from '../../../../@store/products/item-processes/item-processes.service';
import { selectIsOrdersAdmin, selectIsOrdersManager } from '../../../../@store/user/user.selectors';
import { SafeAny } from '../../../../types/safe-any';

interface ItemGroup {
  name: string;
  dataSource: MatTableDataSource<ItemProcessPayload>;
  displayedColumns: string[];
  color: string;
}

@Component({
  selector: 'ciwc-order-item-processes',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSortModule,
    MatPaginatorModule,
    FormatCellValuePipe,
  ],
  standalone: true,
  templateUrl: './order-item-processes.component.html',
  styleUrl: './order-item-processes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderItemProcessesComponent implements OnInit, OnChanges {
  readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);
  readonly isOrdersAdmin = this.store.selectSignal(selectIsOrdersAdmin);

  @Input() parentForm!: FormGroup;
  @Input() itemProcesses: SafeAny = [];
  @Input() viewDisable!: boolean;
  @Input() orderDetails!: singleOrderDetails | null;

  @ViewChildren(MatPaginator) paginators!: QueryList<MatPaginator>;
  @ViewChildren(MatSort) sorters!: QueryList<MatSort>;

  searchTerm = '';

  formArray: FormArray = this.fb.array([]);
  formGroupMap = new Map<number, FormGroup>();

  readonly categories$ = this.store.select(selectItemProcessCategories);

  private _categories: ItemProcessCategory[] = [];

  private readonly itemTypeToCategoryKey: Record<string, string> = {
    OTHER: 'OTHER',
    FILLERS_MISC_LABOR: 'FILLERS_MISC_LABOR',
    // Add more mappings if needed
  };

  ngOnInit() {
    this.initFormArray();
    // Subscribe to categories and cache the latest
    this.categories$.subscribe((cats) => {
      if (Array.isArray(cats)) {
        this._categories = cats;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['itemProcesses'] &&
      changes['itemProcesses'].currentValue !== changes['itemProcesses'].previousValue
    ) {
      this.initFormArray();
    }
    if (changes['viewDisable']) {
      if (this.viewDisable) {
        this.formArray.disable();
      } else {
        this.formArray.enable();
      }
    }
  }

  initFormArray() {
    this.formArray.clear();
    this.formGroupMap.clear();
    if (this.itemProcesses) {
      this.itemProcesses.forEach((row: ItemProcessPayload) => {
        let innerDiameterValue: SafeAny = row.innerDiameter;
        if (Array.isArray(innerDiameterValue)) {
          innerDiameterValue = innerDiameterValue.length > 0 ? Number(innerDiameterValue[0]) : '';
        }
        if (innerDiameterValue === null || innerDiameterValue === undefined) {
          innerDiameterValue = '';
        }

        let outerDiameterValue: SafeAny = row.outerDiameter;
        if (Array.isArray(outerDiameterValue)) {
          outerDiameterValue = outerDiameterValue.length > 0 ? Number(outerDiameterValue[0]) : '';
        }
        if (outerDiameterValue === null || outerDiameterValue === undefined) {
          outerDiameterValue = '';
        }

        const fg = this.fb.group({
          innerDiameter: [innerDiameterValue],
          outerDiameter: [outerDiameterValue],
          units: [row.units],
          ftNeeded: [this.orderDetails?.totalQuantity],
        });
        if (this.viewDisable) {
          fg.disable();
        }
        this.formArray.push(fg);
        this.formGroupMap.set(row.id, fg);
      });

      if (this.isOrdersManager()) {
        // Remove 'ftNeeded' from all column definitions
        Object.keys(this.columnDefinitions).forEach((key) => {
          this.columnDefinitions[key] = this.columnDefinitions[key].filter(
            (col) => col !== 'ftNeeded' && col !== 'pricePerLb' && col !== 'surcharge',
          );
        });
      } else {
        this.itemProcesses = this.itemProcesses.map((row: ItemProcessPayload) => ({
          ...row,
          ftNeeded: this.orderDetails?.totalQuantity,
        }));
      }
    }
  }

  getFormGroup(index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

  // Column definitions for each category
  private readonly columnDefinitions: Record<string, string[]> = {
    FILLERS_MISC_LABOR: [
      'index',
      'description',
      'uniqueId',
      'pricePerUnit',
      'units',
      'ftNeeded',
      'actions',
    ],
    COPPER: ['index', 'description', 'uniqueId', 'lbsPer1000Ft', 'units', 'ftNeeded', 'actions'],
    SINGLE_END: [
      'index',
      'description',
      'uniqueId',
      'ID',
      'pricePerLb',
      'units',
      'ftNeeded',
      'actions',
    ],
    COMPOUNDS: [
      'index',
      'description',
      'uniqueId',
      'ID',
      'OD',
      'specificGravity',
      'pricePerLb',
      'units',
      'ftNeeded',
      'actions',
    ],
    TEFLON_TAPES: [
      'index',
      'description',
      'uniqueId',
      'ID',
      'pricePerLb',
      'units',
      'ftNeeded',
      'actions',
    ],
    ALL_TAPES_AND_FILLERS: [
      'index',
      'description',
      'uniqueId',
      'ID',
      'pricePerLb',
      'units',
      'ftNeeded',
      'actions',
    ],
  };

  // Column header mappings
  readonly columnHeaders = {
    uniqueId: 'Unique ID',
    description: 'Description',
    ID: 'ID',
    pricePerUnit: 'Price/Unit',
    pricePerLb: 'Price/LB',
    lbsPer1000Ft: 'lb/1000 ft',
    constant1: 'Constant 1',
    constant2: 'Constant 2',
    specificGravity: 'Specific Gravity',
    surcharge: 'Surcharge',
    units: 'Units',
    ftNeeded: 'FT Needed',
  };

  // Get columns for a specific group
  getColumnsForGroup(groupKey: string): string[] {
    return (
      this.columnDefinitions[groupKey] || [
        'index',
        'uniqueId',
        'description',
        'ID',
        'pricePerUnit',
        'units',
        'ftNeeded',
        'actions',
      ]
    );
  }

  getColumnHeader(column: string): string {
    return this.columnHeaders[column as keyof typeof this.columnHeaders] || column;
  }

  // Map itemType to color class for expansion panel header
  private getColorClassForType(key: string): string {
    const normalizedKey = this.itemTypeToCategoryKey[key] || key;
    switch (normalizedKey) {
      case 'OTHER':
      case 'FILLERS_MISC_LABOR':
        return 'item-process-fillers-misc-labor';
      case 'COPPER':
        return 'item-process-copper';
      case 'SINGLE_END':
        return 'item-process-single-end';
      case 'COMPOUNDS':
        return 'item-process-compounds';
      case 'TEFLON_TAPES':
        return 'item-process-teflon-tapes';
      case 'ALL_TAPES_AND_FILLERS':
        return 'item-process-all-tapes-and-fillers';
      default:
        return 'item-process-default';
    }
  }

  // Group itemProcesses by itemType
  get itemGroups(): ItemGroup[] {
    // Always show all categories, even if empty
    let categories: ItemProcessCategory[] = [];
    let groups: ItemGroup[] = [];
    // Synchronously get the latest categories (since this is a getter, we need to cache or snapshot)
    if (this._categories) {
      categories = this._categories;
    }
    // Fallback: if no categories loaded yet, use keys from columnDefinitions
    if (!categories.length) {
      categories = Object.keys(this.columnDefinitions).map((name) => ({ key: name, value: name }));
    }
    // Group itemProcesses by itemType
    const grouped: Record<string, ItemProcessPayload[]> = {};

    (this.itemProcesses || []).forEach((item: ItemProcessPayload) => {
      // Map backend itemType to category key
      let categoryKey = this.itemTypeToCategoryKey[item.itemType] || item.itemType;
      // If itemType is null or empty, use FILLERS_MISC_LABOR
      if (item.itemType === 'OTHER') {
        categoryKey = 'FILLERS_MISC_LABOR';
      }

      if (!grouped[categoryKey]) grouped[categoryKey] = [];
      grouped[categoryKey].push(item);
    });

    groups = categories.map((cat) => {
      const categoryKey = cat.key === 'OTHER' ? 'FILLERS_MISC_LABOR' : cat.key;
      const normalizedKey = this.itemTypeToCategoryKey[categoryKey] || categoryKey;
      return {
        name: this.formatCategoryKey(cat.value || cat.key),
        dataSource: new MatTableDataSource(grouped[normalizedKey] || []),
        displayedColumns: this.getColumnsForGroup(normalizedKey),
        color: this.getColorClassForType(normalizedKey),
      };
    });

    return groups;
  }

  removeRow(row: ItemProcessPayload, group: ItemGroup): void {
    // Remove from itemProcesses immutably
    this.itemProcesses = this.itemProcesses.filter((r: ItemProcessPayload) => r.id !== row.id);

    // Remove from group.dataSource by updating its data array
    if (group && group.dataSource) {
      const currentData = group.dataSource.data;
      group.dataSource.data = currentData.filter((r: ItemProcessPayload) => r.id !== row.id);
    }

    // Rebuild formArray and formGroupMap
    this.initFormArray();
  }

  // TrackBy functions for performance
  trackByGroupName(index: number, group: ItemGroup) {
    return group.name;
  }

  /**
   * Returns the itemProcesses array merged with the current form values for each item,
   * matching the ItemProcessPayload interface.
   */
  public getItemProcessesPayload(): ItemProcessPayload[] {
    return (this.itemProcesses || []).map((item: ItemProcessPayload) => {
      const formValue = this.formGroupMap.get(item.id)?.value || {};
      // Use the same mapping as in itemGroups
      const itemTypeKey = item.itemType;
      let category: string;

      if (!itemTypeKey || itemTypeKey === '') {
        category = 'OTHER';
      } else {
        category = itemTypeKey;
      }

      return {
        id: item.id,
        uniqueIdentifier: item.uniqueIdentifier ?? formValue.uniqueIdentifier,
        itemId: item.itemId ? item.itemId : '-',
        itemType: category,
        description: item.description ?? formValue.description,
        lbPer1000ft: item.lbPer1000ft ?? formValue.lbPer1000ft,
        pricePerLb: item.pricePerLb ?? formValue.pricePerLb,
        pricePerUnit: item.pricePerUnit ?? formValue.pricePerUnit,
        specificGravity: item.specificGravity ?? formValue.specificGravity,
        constant1: item.constant1 ?? formValue.constant1,
        constant2: item.constant2 ?? formValue.constant2,
        innerDiameter: formValue.innerDiameter ?? item.innerDiameter,
        outerDiameter: formValue.outerDiameter ?? item.outerDiameter,
        units: formValue.units ?? item.units,
        ftNeeded: this.orderDetails?.totalQuantity ?? this.orderDetails?.totalQuantity,
      };
    });
  }

  public openItemProcessModal(): void {
    const dialogRef = this.dialog.open(CaAddItemProcessesComponent, {});

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => result?.success && result?.category),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result?.data) {
          // Add the new item to the itemProcesses array
          this.itemProcesses = [...this.itemProcesses, result.data];

          // Rebuild formArray and formGroupMap
          this.initFormArray();

          // Trigger change detection
          this.cdr.detectChanges();
        }
      });
  }

  private formatCategoryKey(key: string): string {
    return key
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  readonly onNumericKeyPress = inject(InputValidationService).onNumericKeyPress;
}
